/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import {generateUUID, nullify} from '@dvrd/dvr-controls';
import {DocumentType} from '../util/enums';
import {DefaultListParams, ModelResponse} from '../util/interfaces';
import {deleteDocument, downloadDocument, getDocuments, updateDocument, uploadDocument} from '../util/requestSender';
import {deepClone} from '../util/utils';
import {BaseFields, BaseModel, BaseProps, FieldDefinition, ModelFieldType} from './baseModel';
import {ResponseData} from '@dvrd/fetch';
import IDate from '@dvrd/idate';

interface Props extends BaseProps {
    label?: string;
    file_name?: string;
    file_size?: number;
    file_extension?: string;
    file_type?: string;
    document_type?: DocumentType;
    active?: boolean;
    index?: number;
}

export interface DocumentFields extends BaseFields {
    label: string;
    file_name: string;
    file_size: number;
    file_extension: string;
    file_type: string;
    document_type: DocumentType;
    active: boolean;
    index: number;
}

const fields: DocumentFields = {
    id: '',
    created_at: '',
    updated_at: '',
    label: '',
    file_name: '',
    file_size: 0,
    file_extension: '',
    file_type: '',
    document_type: DocumentType.DOCUMENT,
    active: true,
    index: -1
};

type FieldDefType = keyof DocumentFields;

const fieldsDefinition: Record<FieldDefType, FieldDefinition> = {
    id: new FieldDefinition(ModelFieldType.STRING),
    created_at: new FieldDefinition(ModelFieldType.STRING),
    updated_at: new FieldDefinition(ModelFieldType.STRING, {allowNull: true}),
    label: new FieldDefinition(ModelFieldType.STRING),
    file_name: new FieldDefinition(ModelFieldType.STRING),
    file_size: new FieldDefinition(ModelFieldType.NUMBER),
    file_extension: new FieldDefinition(ModelFieldType.STRING),
    file_type: new FieldDefinition(ModelFieldType.STRING),
    document_type: new FieldDefinition(ModelFieldType.STRING, {defaultValue: DocumentType.DOCUMENT}),
    active: new FieldDefinition(ModelFieldType.BOOLEAN),
    index: new FieldDefinition(ModelFieldType.NUMBER)
};

const ignoreProps: Array<keyof DocumentFields> = [];

type GetAllConfig = DefaultListParams & {active?: boolean; document_type?: DocumentType;};

export default class Document extends BaseModel<Props, DocumentFields, FieldDefType> {
    static getAll = (config: GetAllConfig, callback: ModelResponse<Array<Document>>) => {
        return getDocuments({
            data: {
                limit: config.limit, active: config.active, search: nullify(config.search),
                document_type: config.document_type, before_id: config.beforeID, after_id: config.afterID
            }, callback: (data: ResponseData) => {
                const documents: Array<Document> = data.success ? data.documents.map(
                    (doc: any) => new Document(doc)) : [];
                callback(documents, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
        this.identity = props?.identity ?? `Document.${props?.id ?? generateUUID()}`;
    }

    get updatedAtNullMoment(): IDate | null {
        if(!this.fields.updated_at?.length) return null;
        return new IDate(this.fields.updated_at);
    }

    get label(): string {
        return this.fields.label;
    }

    set label(value: string) {
        this.fields.label = value;
    }

    get fileName(): string {
        return this.fields.file_name;
    }

    set fileName(value: string) {
        this.fields.file_name = value;
    }

    get fileSize(): number {
        return this.fields.file_size;
    }

    set fileSize(value: number) {
        this.fields.file_size = value;
    }

    get fileExtension(): string {
        return this.fields.file_extension;
    }

    set fileExtension(value: string) {
        this.fields.file_extension = value;
    }

    get displayName(): string {
        return this.fields.file_name + this.fields.file_extension;
    }

    get fileType(): string {
        return this.fields.file_type;
    }

    set fileType(value: string) {
        this.fields.file_type = value;
    }

    get documentType(): DocumentType {
        return this.fields.document_type;
    }

    set documentType(value: DocumentType) {
        this.fields.document_type = value;
    }

    get active(): boolean {
        return this.fields.active;
    }

    set active(value: boolean) {
        this.fields.active = value;
    }

    get index(): number {
        return this.fields.index;
    }

    set index(value: number) {
        this.fields.index = value;
    }

    upload = (file: File, callback: ModelResponse<Document>) => {
        return uploadDocument({
            file, document_type: this.documentType, active: this.active, label: this.label,
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.document);
                callback(this, data.success, data);
            }
        });
    }

    update = (callback: ModelResponse<Document>) => {
        return updateDocument({
            data: {document_id: this.id, label: this.label, active: this.active}, callback: (data: ResponseData) => {
                if (data.success) this.build(data.document);
                callback(this, data.success, data);
            }
        });
    }

    download = (callback: ModelResponse<Document>) => {
        return downloadDocument({
            document_id: this.id, callback: (data: ResponseData, response: Response) => {
                if (data.success) {
                    window.open(data.url, '_blank');
                    callback(this, data.success, data);
                } else callback(this, data.success, data);
            }
        });
    };

    delete = (callback: ModelResponse<Document>) => {
        return deleteDocument({
            document_id: this.id, callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        })
    }

    clone = (identityMap?: Map<string, any>): Document => {
        const props = deepClone(this.fields, identityMap);
        return new Document({...props, identity: this.identity});
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
    };
}